import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useGitLabSourceSettingsText,
  useGitLabSourceSettingsViewModel,
} from "@onetasklist/shared/sources/gitlab";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface GitLabSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const GitLabSourceSettings: React.FC<GitLabSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useGitLabSourceSettingsText(t);
  const vm = useGitLabSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [projectsQuery, setProjectsQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.gitlab.sectionProjects",
      )}
    >
      <Field
        orientation="horizontal"
        label={text.projectsLabelText}
        hint={text.projectsHintText}
      >
        <SourceMultiTagPicker
          selectedIds={vm.selectedProjectIds}
          query={projectsQuery}
          placeholder={text.projectsPlaceholderText}
          options={vm.projectOptions}
          onQueryChange={setProjectsQuery}
          onSelectionChange={vm.onSelectProjects}
          minQueryLength={0}
          typeToSearchText={text.projectsHintText}
          isLoading={!vm.projectsLoaded}
        />
      </Field>

      <Field
        orientation="horizontal"
        label={text.assignedToMeLabelText}
        hint={text.assignedToMeHintText}
      >
        <Checkbox
          checked={vm.assignedToMeFixed}
          disabled
          label={text.assignedToMeLabelText}
        />
      </Field>

      {!vm.projectsLoaded && <Text>{text.loadingText}</Text>}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}
    </SettingsSectionCard>
  );
};
